import React, { useCallback, useMemo } from 'react'

import { createColumnHelper } from '@tanstack/react-table'
import { Flex, QuickTip, Switch, Table, Text, Tooltip } from 'govwell-ui'
import { useTableState } from 'govwell-ui/components/Table/use-table-state'
import { UserDataFragment, UserGroup } from 'types/graphql'

import { formatUserName } from 'src/utils'

type StaffMemberData = {
  id: number
  userId: number
  group: UserGroup | null
  isAIStaffMember: boolean
  isOptedOutFromAicaDigest: boolean | null
  isOptedOutFromAicaUsageNotice: boolean | null
  user: UserDataFragment
}

const isEligibleForUsageNotice = (member: StaffMemberData) =>
  member.group === 'Admin' || member.isAIStaffMember

type Props = {
  staffMembers: StaffMemberData[]
  onToggleDigestOptOut: (userId: number, shouldOptOut: boolean) => Promise<void>
  onToggleUsageNoticeOptOut?: (userId: number, shouldOptOut: boolean) => Promise<void>
  showUsageNoticeColumn: boolean
  isProcessing: boolean
}

const StaffDigestTable = ({
  staffMembers,
  onToggleDigestOptOut,
  onToggleUsageNoticeOptOut,
  showUsageNoticeColumn,
  isProcessing,
}: Props) => {
  const getRowId = useCallback((member: StaffMemberData) => `${member.id}`, [])
  const { debouncedSearchQuery, ...tableProps } = useTableState<StaffMemberData>({
    initialState: {
      sorting: [{ id: 'name', desc: false }],
    },
  })

  const filteredStaffMembers = useMemo(() => {
    if (!debouncedSearchQuery) {
      return staffMembers
    }
    const query = debouncedSearchQuery.toLowerCase()
    return staffMembers.filter((member) => {
      const name = formatUserName(member.user).toLowerCase()
      const email = member.user.emailAddress?.toLowerCase() ?? ''
      return name.includes(query) || email.includes(query)
    })
  }, [staffMembers, debouncedSearchQuery])

  const handleDigestToggle = useCallback(
    async (member: StaffMemberData, value: boolean) => {
      await onToggleDigestOptOut(member.userId, !value)
    },
    [onToggleDigestOptOut]
  )

  const handleUsageNoticeToggle = useCallback(
    async (member: StaffMemberData, value: boolean) => {
      await onToggleUsageNoticeOptOut?.(member.userId, !value)
    },
    [onToggleUsageNoticeOptOut]
  )

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<StaffMemberData>()

    return [
      columnHelper.accessor((member) => formatUserName(member.user), {
        id: 'name',
        header: 'Name',
        cell: (info) => <Text.Body>{info.getValue()}</Text.Body>,
      }),
      columnHelper.accessor((member) => member.user.emailAddress, {
        id: 'email',
        header: 'Email',
        cell: (info) => <Text.Body>{info.getValue()}</Text.Body>,
        enableSorting: false,
      }),
      columnHelper.display({
        id: 'digest',
        header: () => (
          <Flex direction="row" gap={1} align="center">
            <span>Monthly Digest</span>
            <QuickTip content="Monthly summary of Community Assistant value metrics. All active staff are eligible." />
          </Flex>
        ),
        enableSorting: false,
        cell: ({ row }) => {
          const member = row.original
          const isSubscribed = !member.isOptedOutFromAicaDigest
          return (
            <Switch
              value={isSubscribed}
              onValueChange={(value) => handleDigestToggle(member, value)}
              isDisabled={isProcessing}
              ariaLabel={`Toggle digest emails for ${formatUserName(member.user)}`}
            />
          )
        },
      }),
      ...(showUsageNoticeColumn
        ? [
            columnHelper.display({
              id: 'usageNotice',
              header: () => (
                <Flex direction="row" gap={1} align="center">
                  <span>Usage Notice</span>
                  <QuickTip content="Sent when the monthly AI response limit is exceeded. Only organization admins and AI Admins are eligible." />
                </Flex>
              ),
              enableSorting: false,
              cell: ({ row }) => {
                const member = row.original
                const isEligible = isEligibleForUsageNotice(member)
                if (!isEligible) {
                  return (
                    <Tooltip content="Only organization admins and AI Admins can receive usage notice emails">
                      <div>
                        <Switch
                          value={false}
                          onValueChange={() => {}}
                          isDisabled
                          ariaLabel={`Usage notice emails not available for ${formatUserName(member.user)}`}
                        />
                      </div>
                    </Tooltip>
                  )
                }
                const isSubscribed = !member.isOptedOutFromAicaUsageNotice
                return (
                  <Switch
                    value={isSubscribed}
                    onValueChange={(value) => handleUsageNoticeToggle(member, value)}
                    isDisabled={isProcessing}
                    ariaLabel={`Toggle usage notice emails for ${formatUserName(member.user)}`}
                  />
                )
              },
            }),
          ]
        : []),
    ]
  }, [handleDigestToggle, handleUsageNoticeToggle, isProcessing, showUsageNoticeColumn])

  return (
    <Table
      columns={columns}
      data={filteredStaffMembers}
      enablePagination
      enableSearching
      searchInputPlaceholder="Search by name or email..."
      isPaginatedOnClient
      getRowId={getRowId}
      isSortedOnClient
      emptyStateMessage="No staff members found."
      {...tableProps}
    />
  )
}

export default React.memo(StaffDigestTable)
